import * as React from 'react';
import { Clock } from 'lucide-react';

import { fetchJson, type ActiveOrg } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import Upgrade from '@/pages/Upgrade';

const SUPPORT_EMAIL = 'esaycall@abaye.co';

function formatRemaining(ms: number): string {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours >= 1) return `${hours} שעות ו-${minutes} דקות`;
  return `${minutes} דקות`;
}

function TrialBanner({ org }: { org: ActiveOrg }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
      <Clock className="h-4 w-4 shrink-0" />
      <span>
        חשבון ניסיון - נותרו <strong>{formatRemaining(org.trialMsRemaining)}</strong>.
      </span>
      <a
        href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`שדרוג Easy Call - ${org.name}`)}`}
        className="font-medium underline underline-offset-2"
      >
        לשדרוג לחשבון מלא
      </a>
    </div>
  );
}

/**
 * Blocks the app for expired trial orgs (showing the Upgrade screen) and shows a
 * countdown banner while a trial is still active. Super admins bypass entirely.
 */
export function TrialGate({
  isSuperAdmin,
  children
}: {
  isSuperAdmin: boolean;
  children: React.ReactNode;
}) {
  const [state, setState] = React.useState<
    { loading: true } | { loading: false; org: ActiveOrg | null }
  >({ loading: true });

  React.useEffect(() => {
    if (isSuperAdmin) return;
    let cancelled = false;
    fetchJson<ActiveOrg>('/api/me/active-org')
      .then((o) => !cancelled && setState({ loading: false, org: o }))
      .catch(() => !cancelled && setState({ loading: false, org: null }));
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  if (isSuperAdmin) return <>{children}</>;

  if (state.loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const org = state.org;
  if (org && org.plan === 'trial' && org.trialExpired) {
    return <Upgrade org={org} />;
  }

  return (
    <>
      {org && org.plan === 'trial' && !org.trialExpired && <TrialBanner org={org} />}
      {children}
    </>
  );
}
